'use client';

import { BigNum } from '@velocity-exchange/sdk';
import { useEffect } from 'react';
import { MIN_LEFTOVER_SOL } from '@velocity-exchange/common';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { fetchAccountCreationRent } from 'src/utils/rent';

export type AccountCreationCostBreakdown = {
	loaded: boolean; // Whether full cost breakdown has been loaded
	totalCost: BigNum; // The total cost to create a new account
	baseAccountRent: BigNum; // The base solana network account rent component of the fee (refundable)
	newAccountDonation: BigNum; // The drift donation component of the fee (non-refundable)
	extraRent: BigNum; // The dynamic extra rent component of the fee (refundable)
	minSolToDeposit: BigNum; // The minimum amount of SOL required to make a new account
	minSolForFees: BigNum; // The minimum amount of SOL required to cover fees
};

const useSyncAccountCreationCost = () => {
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();

	const setVelocityStore = useVelocityStore((state) => state.set);

	useEffect(() => {
		if (!velocityClient || !velocityClientIsReady) return;

		let cancelled = false;

		(async () => {
			try {
				const rent = await fetchAccountCreationRent(velocityClient.connection);
				if (cancelled) return;

				const cost = rent.baseCost;

				setVelocityStore((s) => {
					s.accountCreationCost.loaded = true;
					s.accountCreationCost.totalCost = cost;
					s.accountCreationCost.baseAccountRent = rent.userAccountRent;
					// Preserve prior semantics: extraRent = totalCost - baseAccountRent
					// (= donation + swift rent).
					s.accountCreationCost.extraRent = cost.sub(rent.userAccountRent);
					s.accountCreationCost.minSolToDeposit = cost.add(MIN_LEFTOVER_SOL);
					s.accountCreationCost.minSolForFees = MIN_LEFTOVER_SOL;
				});
			} catch (err) {
				console.error('Failed to fetch account creation rent', err);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [velocityClient, velocityClientIsReady, setVelocityStore]);
};

export const useAccountCreationCost = () => {
	const accountCreationCost = useVelocityStore(
		(state) => state.accountCreationCost
	);

	useSyncAccountCreationCost();

	return accountCreationCost;
};
